/**
 * Handles the alternate Krishna artwork without loading the full second image
 * until a customer hovers, focuses, or taps the artwork.
 */
class SkScriptureFoundation extends HTMLElement {
  #abortController = new AbortController();
  #canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  connectedCallback() {
    const { signal } = this.#abortController;
    this.addEventListener('pointerover', this.#handleIntent, { signal });
    this.addEventListener('focusin', this.#handleIntent, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleIntent = (event) => {
    if (!(event.target instanceof Element)) return;
    const switcher = event.target.closest('[data-krishna-switch]');
    if (switcher && this.contains(switcher)) this.#loadAlternate(switcher);
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const switcher = event.target.closest('[data-krishna-switch]');
    if (!(switcher instanceof HTMLButtonElement) || !this.contains(switcher)) return;

    // Mouse and trackpad users get a temporary hover swap. Touch and keyboard
    // users can toggle the alternate artwork with the same accessible button.
    if (this.#canHover.matches && event.detail > 0) return;

    event.preventDefault();
    this.#loadAlternate(switcher);
    const active = !switcher.classList.contains('is-alternate');
    switcher.classList.toggle('is-alternate', active);
    switcher.setAttribute('aria-pressed', String(active));
  };

  #loadAlternate(switcher) {
    const image = switcher.querySelector('[data-krishna-alternate]');
    if (!(image instanceof HTMLImageElement) || !image.dataset.src) return;

    const reveal = () => switcher.classList.add('has-alternate-image');
    image.addEventListener('load', reveal, { once: true });
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    image.src = image.dataset.src;
    delete image.dataset.src;
    delete image.dataset.srcset;

    if (image.complete && image.naturalWidth > 80) reveal();
  }
}

if (!customElements.get('sk-scripture-foundation')) {
  customElements.define('sk-scripture-foundation', SkScriptureFoundation);
}
